import { useCreateEvent } from '@/features/events/hooks/useCreateEvent';
import { EventType } from '@/features/events/types/EventType';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseButton from '../../../../pages/new-platform/components/CloseButton';
import { saveEvent } from '../../../../utils/apis/user-service';
import { useExpertsStore } from '../../../../utils/stores/useExpertsStore';
import { useUserStore } from '../../../../utils/stores/useUserStore';
import { ExpertRole } from '../../../experts/types/ExpertRole';
import useGetChatSessions from '../../hooks/useGetChatSessions';
import { useMessages } from '../../hooks/useMessages';
import { ChatSession } from '../../types/ChatSession';
import { getExpertAvatar } from '../../utils/getExpertAvatar';
import { getExpertInfo } from '../../utils/getExpertInfo';
import { handleExpertName } from '../../utils/handleExpertName';
import ChatBox from './ChatBox';
import { SendBar } from './SendBar';

interface Props {
  chatSession: ChatSession;
}

export const ChatSessionScreen: FC<Props> = ({ chatSession }) => {
  const { sendEvent } = useCreateEvent();
  const { userData } = useUserStore();
  const email = userData.email;
  const navigate = useNavigate();
  const { refetch } = useGetChatSessions();
  const { experts } = useExpertsStore();

  const {
    messages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
  } = useMessages(chatSession.id, true);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!email) return;
    saveEvent(email, "page_view", () => { }, () => { }, "chat_session_page");

    void sendEvent({
      event: EventType.PAGE_VIEW,
      sub_category: "chat_session_page",
      event_metadata: { session_id: chatSession.id },
    });
  }, []);

  const { expertRole, expertRoleString, expertName, expertFamilyName, expertAvatar } =
    getExpertInfo(chatSession.expert_id, experts);

  const onCloseClick = async () => {
    navigate("/chat");
    if (!email) return;
    saveEvent(
      email,
      `back_button_from_chat_${chatSession.id}_clicked`,
      () => { },
      () => { },
      "chat_session_page"
    );
    await sendEvent({
      event: EventType.BUTTON_CLICK,
      sub_category: "back_button",
      event_metadata: { session_id: chatSession.id },
    });
  };

  if (isError) {
    return <div>Error loading messages</div>;
  }

  return (
    <div className="flex flex-col min-h-0 w-full bg-white fixed inset-0">
      {/* HEADER - Always visible */}
      <div className='flex items-center justify-between shrink-0 z-10 bg-white shadow-lg pt-[30px] p-[8px] md:px-[50px]'>
        <div className='flex items-center mb-[5px]'>
          {getExpertAvatar(expertAvatar)}
          <div className='flex flex-col'>
            <div className='text-[22px] leading-[33px] font-bold'>
              {handleExpertName(expertRole, `${expertName} ${expertFamilyName}`)}
            </div>
            {
              expertRole !== ExpertRole.Admin &&
              <div className='font-semibold text-[12px] text-holifya-blue'>
                {expertRoleString}
              </div>
            }
          </div>
        </div>
        <div className='mt-[10px]'>
          <CloseButton onClick={onCloseClick} />
        </div>
      </div>

      {/* CHAT + SEND */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* ChatBox - scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-[8px] md:px-[50px]">
          <ChatBox
            messages={messages ?? []}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
          />
        </div>

        {/* SendBar - sticky */}
        <div className="shrink-0 px-[8px] md:px-[50px] pb-[10px]">
          <SendBar chatSessionId={chatSession.id} />
        </div>
      </div>
    </div>
  );
};
